export type ProductColor = {
  name: string;
  value: string;
  image?: string;
};

export type DealType = "limited" | "special" | "seasonal" | "premium" | "exclusive";
export type DeliveryType = "delivery" | "fast" | "stock";

export interface FashionProductItem {
  id: string | number;
  _id?: string;
  ssin?: string;
  badge: string;
  title: string;
  description: string;
  brand?: {
    _id?: string;
    name?: string;
    logo?: string;
  };
  categoryHierarchy?: {
    superCategory?: string;
    category?: string;
    subCategory?: string;
    childCategory?: string;
  };
  image: string;
  images?: string[];
  colors: ProductColor[];
  extraColors: number;
  rating: number;
  reviews: string;
  deal: string;
  dealType: DealType;
  price: number;
  mrp: number;
  discountPercent: number;
  savingsAmount?: number;
  delivery: string;
  deliveryType: DeliveryType;
  optionLabel?: string;
  sizes?: string[];
  variationTheme?: string[];
  variationMatrix?: Record<string, string[]>;
  variations?: any[];
  rawProduct?: any;
}

const COLOR_MAP: Record<string, string> = {
  black: "#202020",
  white: "#f5f5f5",
  silver: "#d1d5db",
  "space gray": "#52525b",
  "space black": "#18181b",
  midnight: "#0f172a",
  "deep purple": "#581c87",
  gold: "#eab308",
  "rose gold": "#f43f5e",
  starlight: "#fef08a",
  wood: "#78350f",
  titanium: "#71717a",
  graphite: "#3f3f46",
  navy: "#17283e",
  blue: "#2563eb",
  royalblue: "#1d4ed8",
  skyblue: "#38bdf8",
  wine: "#89384c",
  maroon: "#800000",
  red: "#dc2626",
  pink: "#ec4899",
  rose: "#da3465",
  green: "#16a34a",
  olive: "#65a30d",
  mint: "#10b981",
  cream: "#fef3c7",
  beige: "#d6c7a1",
  ivory: "#fffff0",
  grey: "#6b7280",
  gray: "#6b7280",
  charcoal: "#374151",
  yellow: "#eab308",
  mustard: "#ca8a04",
  orange: "#f97316",
  amber: "#d97706",
  brown: "#78350f",
  purple: "#9333ea",
  lavender: "#c084fc",
};

export const getColorHex = (name?: string): string => {
  if (!name) return "#222222";
  const normalized = name.toLowerCase().trim();
  if (COLOR_MAP[normalized]) return COLOR_MAP[normalized];
  for (const [key, hex] of Object.entries(COLOR_MAP)) {
    if (normalized.includes(key)) return hex;
  }
  return "#4a5568";
};

export const formatPrice = (value: number): string =>
  new Intl.NumberFormat("en-IN").format(Math.round(value));

export const formatReviewCount = (count?: number | string): string => {
  if (!count) return "0";
  const num = typeof count === "string" ? parseInt(count, 10) : count;
  if (isNaN(num) || num <= 0) return "0";
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return `${num}`;
};

export const mapBrowseProductToFashion = (
  raw: any,
  gender: "men" | "women",
  index: number
): FashionProductItem => {
  const variations: any[] = raw.variations || raw.variants || [];
  const offer = raw.featuredOffer || {};
  const price = offer.finalPrice || offer.displayPrice || offer.unitPrice || raw.price || 0;
  const mrp = offer.mrp || (price > 0 ? (offer.discountPercent ? Math.round(price / (1 - offer.discountPercent / 100)) : price) : 0);
  const discountPercent =
    offer.discountPercent || (mrp > price && mrp > 0 ? Math.round(((mrp - price) / mrp) * 100) : 0);

  // Determine badge from real data
  let badge = "";
  if (raw.isSponsored) {
    badge = "SPONSORED";
  } else if (raw.badge) {
    badge = raw.badge;
  } else if (raw.isBestSeller) {
    badge = "BEST SELLER";
  } else if (raw.isTrending) {
    badge = "TRENDING";
  } else if (raw.brand?.name) {
    badge = `${raw.brand.name.toUpperCase()} OFFICIAL`;
  }

  // Extract colors with corresponding variant images
  const colorMap = new Map<string, ProductColor>();

  // 1. From raw.colors string array
  if (raw.colors && Array.isArray(raw.colors) && raw.colors.length > 0) {
    raw.colors.forEach((cName: string) => {
      if (!cName || typeof cName !== "string") return;
      const vMatch = variations.find((v) => {
        const attr = v.variationAttributes || v.attributes || {};
        const colorVal = attr.Color || attr.color || attr.Colour || attr.colour;
        return (
          colorVal &&
          typeof colorVal === "string" &&
          colorVal.toLowerCase().trim() === cName.toLowerCase().trim()
        );
      });

      colorMap.set(cName.toLowerCase().trim(), {
        name: cName,
        value: getColorHex(cName),
        image: vMatch?.mainImage || vMatch?.images?.[0] || raw.mainImage || raw.image || "",
      });
    });
  }

  // 2. From variations attributes
  variations.forEach((v) => {
    const attr = v.variationAttributes || v.attributes || {};
    const cName = attr.Color || attr.color || attr.Colour || attr.colour;
    if (cName && typeof cName === "string") {
      const normalized = cName.toLowerCase().trim();
      if (!colorMap.has(normalized)) {
        colorMap.set(normalized, {
          name: cName,
          value: getColorHex(cName),
          image: v.mainImage || v.images?.[0] || raw.mainImage || raw.image || "",
        });
      }
    }
  });

  const colorsList = Array.from(colorMap.values());

  // Extract secondary variation theme (anything other than Color)
  const variationThemes: string[] = Array.isArray(raw.variationTheme) ? raw.variationTheme : [];
  const nonColorThemes = variationThemes.filter(
    (t: any) => typeof t === "string" && !["color", "colour"].includes(t.toLowerCase().trim())
  );

  let optionLabel = "Size";
  const optionSet = new Set<string>();

  // 1. Try to find non-color theme with values from variationMatrix or variations
  for (const theme of nonColorThemes) {
    if (raw.variationMatrix && Array.isArray(raw.variationMatrix[theme])) {
      raw.variationMatrix[theme].forEach((val: any) => {
        if (val && typeof val === "string" && val.trim()) {
          optionSet.add(val.trim());
        }
      });
    }
    variations.forEach((v) => {
      const attr = v.variationAttributes || v.attributes || {};
      for (const [k, vVal] of Object.entries(attr)) {
        if (k.toLowerCase() === theme.toLowerCase() && typeof vVal === "string" && vVal.trim()) {
          optionSet.add(vVal.trim());
        }
      }
    });

    if (optionSet.size > 0) {
      optionLabel = theme;
      break;
    }
  }

  // 2. If nothing found from variationTheme, inspect any non-color key in variationMatrix
  if (optionSet.size === 0 && raw.variationMatrix && typeof raw.variationMatrix === "object") {
    for (const [key, vals] of Object.entries(raw.variationMatrix)) {
      if (!["color", "colour"].includes(key.toLowerCase().trim()) && Array.isArray(vals) && vals.length > 0) {
        vals.forEach((v: any) => {
          if (v && typeof v === "string" && v.trim()) optionSet.add(v.trim());
        });
        if (optionSet.size > 0) {
          optionLabel = key;
          break;
        }
      }
    }
  }

  // 3. If still empty, check common attribute names in variations
  if (optionSet.size === 0) {
    const commonKeys = ["Size", "Storage", "Material", "Capacity", "RAM"];
    for (const key of commonKeys) {
      variations.forEach((v) => {
        const attr = v.variationAttributes || v.attributes || {};
        for (const [k, vVal] of Object.entries(attr)) {
          if (k.toLowerCase() === key.toLowerCase() && typeof vVal === "string" && vVal.trim()) {
            optionSet.add(vVal.trim());
          }
        }
      });
      if (optionSet.size > 0) {
        optionLabel = key;
        break;
      }
    }
  }

  const superCategoryLower = (raw.categoryHierarchy?.superCategory || "").toLowerCase();
  const isFashion =
    superCategoryLower === "men" ||
    superCategoryLower === "women" ||
    superCategoryLower === "fashion";

  const sizes = optionSet.size > 0 ? Array.from(optionSet) : [];
  const finalColors = colorsList;

  // Deal badge & type from real product data
  let deal = "";
  let dealType: DealType = "limited";
  if (offer.deal && (offer.deal.title || offer.deal.name)) {
    deal = offer.deal.title || offer.deal.name;
    dealType = "limited";
  } else if (offer.couponDiscount && offer.couponDiscount > 0) {
    deal = `Save ₹${formatPrice(offer.couponDiscount)} with Coupon`;
    dealType = "special";
  } else if (discountPercent >= 15) {
    deal = `${discountPercent}% OFF`;
    dealType = "limited";
  }

  // Delivery badge & type from real product data
  let delivery = "";
  let deliveryType: DeliveryType = "delivery";
  if (raw.stockStatus === "low_stock" || (offer.availableQty !== undefined && offer.availableQty < 5 && offer.availableQty > 0)) {
    delivery = "Only Few Left in Stock";
    deliveryType = "stock";
  } else if (offer.fastDelivery || offer.sameDayDelivery) {
    delivery = "Fast Delivery Available";
    deliveryType = "fast";
  } else if (offer.nextDayDelivery) {
    delivery = "Next Day Delivery";
    deliveryType = "fast";
  } else if (offer.freeDelivery) {
    delivery = "Free Delivery";
    deliveryType = "delivery";
  } else if (offer.estimatedDeliveryDays) {
    delivery = `Delivery in ${offer.estimatedDeliveryDays} days`;
    deliveryType = "delivery";
  }

  const description = raw.shortDescription || raw.description || "";

  const rating =
    typeof raw.reviews?.averageRating === "number" && raw.reviews.averageRating > 0
      ? raw.reviews.averageRating
      : typeof offer.sellerRating === "number" && offer.sellerRating > 0
      ? offer.sellerRating
      : 0;

  const reviews = formatReviewCount(
    raw.reviews?.totalReviews || raw.reviews?.totalCount || (raw.ratingsCount ? raw.ratingsCount : 0)
  );

  return {
    id: raw._id || raw.ssin || raw.id || `product-${index}`,
    _id: raw._id,
    ssin: raw.ssin,
    badge,
    title: raw.name || raw.title || "",
    description,
    brand: raw.brand
      ? {
          _id: raw.brand._id,
          name: raw.brand.name,
          logo: raw.brand.logo,
        }
      : undefined,
    categoryHierarchy: raw.categoryHierarchy,
    image: raw.mainImage || raw.image || "",
    images: variations[0]?.images || (raw.mainImage ? [raw.mainImage] : []),
    colors: finalColors,
    extraColors: Math.max(0, finalColors.length - 4),
    rating: parseFloat(rating.toFixed(1)),
    reviews,
    deal,
    dealType,
    price,
    mrp: Math.max(mrp, price),
    discountPercent,
    savingsAmount: offer.savingsAmount || (mrp > price ? mrp - price : 0),
    delivery,
    deliveryType,
    optionLabel,
    sizes,
    variationTheme: raw.variationTheme || [],
    variationMatrix: raw.variationMatrix,
    variations,
    rawProduct: raw,
  };
};
